// src/components/documents/DocumentConfirmModal.jsx - V2 ENTERPRISE PORTAL
// ═══════════════════════════════════════════════════════════════════════════
// ⚠️ DOCUMENT CONFIRM MODAL — Airbnb/Zillow/QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 V2: Portal a document.body — stacking context independiente
// 🔥 V2: Dark mode automático
// 🔥 ULTRA ROBUSTO:    Confirmación con doble barrera (título + botón rojo)
// 🔥 ULTRA RÁPIDO:     GPU-accelerated + respuesta instantánea
// 🔥 ULTRA EFICIENTE:  React.memo + zero estado innecesario
// 🔥 ULTRA PROFESIONAL: Animación de entrada + focus trap + accesibilidad
// ═══════════════════════════════════════════════════════════════════════════

import React, { memo, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { usePortal, useBodyScrollLock, useEscapeKey, portalize } from '../../utils/portalUtils';

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 CONSTANTES DE ANIMACIÓN
// ═══════════════════════════════════════════════════════════════════════════

const MODAL_ANIMATION = {
  overlay: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  panel: {
    initial: { scale: 0.9, opacity: 0, y: 20 },
    animate: { scale: 1, opacity: 1, y: 0 },
    exit: { scale: 0.9, opacity: 0, y: 20 },
    transition: { type: 'spring', stiffness: 450, damping: 30, mass: 0.7 },
  },
  icon: {
    initial: { scale: 0, rotate: -90 },
    animate: { scale: 1, rotate: 0 },
    transition: { delay: 0.1, type: 'spring', stiffness: 300, damping: 20 },
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// ⚠️ COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const DocumentConfirmModal = memo(function DocumentConfirmModal({
  isOpen,
  title = 'Excluir documento',
  message,
  confirmLabel = 'Confirmar exclusão',
  cancelLabel = 'Cancelar',
  onConfirm,
  onCancel,
  isMobile,
  isProcessing = false,
}) {
  // ─── Hooks ──────────────────────────────────────────────────────────
  const mounted = usePortal();
  const cancelRef = useRef(null);

  // ✅ Portal hooks
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, isProcessing ? null : onCancel);

  // ─── Focus trap: foco en Cancelar al abrir ──────────────────────────
  useEffect(() => {
    if (isOpen && cancelRef.current) {
      setTimeout(() => cancelRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // ─── Handlers ────────────────────────────────────────────────────────
  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget && !isProcessing) {
        onCancel?.();
      }
    },
    [onCancel, isProcessing]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        {...MODAL_ANIMATION.overlay}
        className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-[99999] p-4"
        onClick={handleOverlayClick}
        aria-modal="true"
        role="alertdialog"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
      >
        <motion.div
          {...MODAL_ANIMATION.panel}
          className={`
            bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md
            shadow-2xl border border-slate-200 dark:border-slate-700
            ${isMobile ? 'p-5' : 'p-6'}
          `}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ═══════════════════════════════════════════════════════════ */}
          {/* ÍCONO DE ADVERTENCIA                                        */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <motion.div
            {...MODAL_ANIMATION.icon}
            className="mx-auto mb-4 flex items-center justify-center"
          >
            <div
              className={`
                rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center
                ${isMobile ? 'w-12 h-12' : 'w-14 h-14'}
              `}
            >
              <AlertTriangle
                size={isMobile ? 24 : 28}
                className="text-red-600 dark:text-red-400"
                strokeWidth={1.5}
              />
            </div>
          </motion.div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* TEXTO DE CONFIRMACIÓN                                       */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="text-center mb-6">
            <h3
              id="confirm-title"
              className={`font-bold text-slate-900 dark:text-slate-100 mb-2 ${
                isMobile ? 'text-base' : 'text-lg'
              }`}
            >
              {title}
            </h3>
            <p
              id="confirm-message"
              className={`text-slate-600 dark:text-slate-400 ${
                isMobile ? 'text-sm' : 'text-sm'
              }`}
            >
              {message}
            </p>
          </div>

          {/* ═══════════════════════════════════════════════════════════ */}
          {/* BOTONES DE ACCIÓN                                           */}
          {/* ═══════════════════════════════════════════════════════════ */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
            {/* Cancelar */}
            <button
              ref={cancelRef}
              onClick={onCancel}
              disabled={isProcessing}
              className={`
                px-4 py-3 sm:py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl
                text-slate-700 dark:text-slate-300 font-medium
                hover:bg-slate-50 dark:hover:bg-slate-800 active:bg-slate-100 dark:active:bg-slate-700
                transition-all duration-200 active:scale-95
                disabled:opacity-50 disabled:cursor-not-allowed
                ${isMobile ? 'text-sm' : 'text-sm'}
              `}
            >
              {cancelLabel}
            </button>

            {/* Confirmar */}
            <button
              onClick={onConfirm}
              disabled={isProcessing}
              className={`
                px-5 py-3 sm:py-2.5 rounded-xl
                text-white font-semibold
                transition-all duration-200 active:scale-95
                shadow-lg shadow-red-600/20
                disabled:opacity-50 disabled:cursor-not-allowed
                flex items-center justify-center gap-2
                ${isMobile ? 'text-sm' : 'text-sm'}
              `}
              style={{
                backgroundColor: '#e11d48',
              }}
            >
              {isProcessing ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  <span>Excluindo...</span>
                </>
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return portalize(mounted, modalContent);
});

// ═══════════════════════════════════════════════════════════════════════════
// 📤 EXPORT
// ═══════════════════════════════════════════════════════════════════════════

DocumentConfirmModal.displayName = 'DocumentConfirmModal';
export default DocumentConfirmModal;